import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import Modal from "bootstrap/js/dist/modal";
import { useHeaderFilters } from "../../context/HeaderFilterContext";
// AvatarEditor provides an interactive canvas for users to crop, zoom, and rotate their uploaded image into a circular avatar
import AvatarEditor from "react-avatar-editor";
import Button from "antd/es/button";
import { FaCamera as RawFaCamera } from "react-icons/fa";
import { Tab, Nav } from "react-bootstrap";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";

// Cast RawFaCamera to an SVG component so TypeScript allows JSX usage
const FaCamera = RawFaCamera as React.FC<React.SVGProps<SVGSVGElement>>;

interface Props {
  skipSaveApiCall?: boolean;
  name?: string;
  designation?: string;
  imageUrl?: string;
}

interface Breadcrumb {
  title: string;
  path?: string;
  onClick?: () => void;
}
interface DropdownOption {
  id: string;
  name: string;
}
interface Statuses {
  id: string;
  name: string;
}
interface BankDetails {
  account_number: string;
  account_name: string;
  branch_name: string;
  ifsc: string;
}
interface UserRole {
  id: string;
  name: string;
}
interface Gender {
  id: string;
  name: string;
}
interface Designations {
  id: string;
  name: string;
}
interface ReportingHead {
  id: string;
  name: string;
}
interface EmployeeData {
  id: string;
  name: string;
  designation?: { title: string };
  image_url?: string;
  date_of_birth?: string;
  date_of_joining?: string;
  bank_details?: {
    account_number?: string;
    account_name?: string;
    branch_name?: string;
    ifsc?: string;
  };
}

const EmployeeImage: React.FC<Props> = ({
  skipSaveApiCall = false,
  name: passedName,
  designation: passedDesignation,
  imageUrl: passedImageUrl,
}) => {
  const { user, setUser } = useAuth();
  const { id: employeeId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 767);
  const { setFilters } = useHeaderFilters();
  const location = useLocation();
  const modalInstance = useRef<Modal | null>(null);
  // state
  const [employee, setEmployee] = useState<EmployeeData | null>(null);
  //   const [imageUrl, setImageUrl] = useState<string>("")
  // const [imageUrl, setImageUrl] = useState<string>(propImageUrl || "");
  const [imageUrl, setImageUrl] = useState<string>(passedImageUrl || "");
  const [imageLoaded, setImageLoaded] = useState(false);

  const [loading, setLoading] = useState(true);
  const [designations, setDesignations] = useState<DropdownOption[]>([]);
  // crop state
  const [error, setError] = useState("");
  const [genders, setGenders] = useState<DropdownOption[]>([]);
  const [statuses, setStatuses] = useState<DropdownOption[]>([]);
  const [userRoles, setUserRoles] = useState<DropdownOption[]>([]);
  const [reportingHeads, setReportingHeads] = useState<DropdownOption[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scale, setScale] = useState<number>(1);
  const editorRef = useRef<AvatarEditor>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  let bsModal: Modal | null = null;

  // breadcrumbs from location.state
  const locState = (location.state || {}) as {
    from?: string;
    payrollUrl?: string;
    attUrl?: string;
    leaveUrl?: string;
    monthYear?: string;
    previousUrl?: string;
    employeeName?: string;
  };
  const {
    from,
    payrollUrl,
    monthYear,
    previousUrl,
    employeeName,
    attUrl,
    leaveUrl,
  } = locState;
  const safePreviousUrl = previousUrl || "/employees";
  const safePayrollUrl = payrollUrl || "/payroll";
  const safeAttlUrl = attUrl || "/attendance-view/A";
  const safeLeaveUrl = leaveUrl || "/leave/leave-request";
  const safeMonthYear = monthYear || "";
  const safeEmployeeName = employeeName || "";

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // build breadcrumbs array
  const breadcrumbs: Breadcrumb[] =
    from === "payroll"
      ? [
          {
            title: "Payroll",
            onClick: () =>
              navigate(safePayrollUrl, {
                state: { from: "payroll", monthYear },
                replace: true,
              }),
          },
          ...(safeMonthYear
            ? [
                {
                  title: safeMonthYear,
                  onClick: () =>
                    navigate(safePayrollUrl, {
                      state: { from: "payroll", monthYear },
                      replace: true,
                    }),
                },
              ]
            : []),

          { title: employeeName || employee?.name || "Profile" },
        ]
      : from === "attendance"
      ? [
          {
            title: "Attendance",
            onClick: () =>
              navigate(safeAttlUrl, {
                state: { from: "attendance" },
                replace: true,
              }),
          },
          { title: employeeName || employee?.name || "Profile" },
        ]
      : from === "leave"
      ? [
          {
            title: "Leave",
            onClick: () =>
              navigate(safeLeaveUrl, {
                state: { from: "leave" },
                replace: true,
              }),
          },
          { title: employeeName || employee?.name || "Profile" },
        ]
      : from === "employees"
      ? [
          {
            title: "Employee",
            onClick: () => navigate(safePreviousUrl, { replace: true }),
          },
          {
            title: employeeName || employee?.name || "Profile",
          },
        ]
      : [
          {
            title: localStorage.getItem("pageTitle") || "Profile",
          },
        ];

  useEffect(() => {
    if (skipSaveApiCall) {
      setEmployee({
        id: "", // required by type
        name: passedName || "",
        designation: { title: passedDesignation || "" },
        image_url: passedImageUrl || "",
      });

      setImageUrl(passedImageUrl || "");
      setLoading(false);
      return;
    }

    const formatDate = (date: string | undefined) =>
      date ? moment(date, "DD-MM-YYYY").format("DD-MMM-YYYY") : "";

    const formatLeavingDate = (date: string | undefined) =>
      date ? moment(date, "YYYY-MM-DD").format("DD-MMM-YYYY") : "";

    const fetchEmployee = async () => {
      try {
        const res = await axiosInstance.get(`/edit-employee/${employeeId}`);

        if (res.status === 429) {
          window.location.href = "/internalserver";
          return;
        }

        const data = res.data;
        const emp = data.data;

        setEmployee({
          ...emp,
          date_of_birth: formatDate(emp.date_of_birth),
          date_of_joining: formatDate(emp.date_of_joining),
          date_of_leaving: formatLeavingDate(emp.date_of_leaving),
          permanent_wfh: !!emp.is_permenant_wfh,
          by_pass_attendance: !!emp.by_pass_attendance_check,
          gender: emp.gender,
          user_role: emp.role_name,
          bank_details: emp.bank_details || {},
        });
        setImageUrl(emp.image_url || "");
        const designations: Designations[] = Object.entries(
          res.data.designations || {}
        ).map(([id, name]) => ({
          id,
          name: name as string,
        }));
        setDesignations(designations);
        const reportingHeads: ReportingHead[] = Object.values(
          res.data.reporting_head || {}
        ).map((emp: any) => ({
          id: emp.id,
          name: emp.name,
        }));
        setReportingHeads(reportingHeads);
        const genders: Gender[] = Object.entries(res.data.gender || {}).map(
          ([id, name]) => ({
            id,
            name: name as string,
          })
        );
        setGenders(genders);
        const statuses: Statuses[] = Object.entries(res.data.status || {}).map(
          ([id, name]) => ({
            id,
            name: name as string,
          })
        );
        setStatuses(statuses);
        const userRoles: UserRole[] = Object.entries(
          res.data.userrole || {}
        ).map(([id, name]) => ({
          id,
          name: name as string,
        }));
        setUserRoles(userRoles);
      } catch (err: any) {
        if (err.response) {
          if (err.response.status === 403) {
            localStorage.removeItem("token");
            window.location.href = "/";
            return;
          }
          if (err.response.status === 404) {
            navigate("/employee", {
              state: { error: "Employee not found" },
            });
            return;
          }
        }
        setError("Failed to load employee details.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [
    employeeId,
    navigate,
    skipSaveApiCall,
    passedName,
    passedDesignation,
    passedImageUrl,
  ]);

  useEffect(() => {
    const ProfileMobileFilters = () => (
      <div className="px-2 px-md-2">
        <div className="designation-filters d-flex align-items-center flex-wrap ">
          <div className="d-flex align-items-center gap-2  toggle-att-employeeSalaryMedia">
            {/* Breadcrumb */}
            <nav
              aria-label="breadcrumb"
              className="mt-3 text-sm text-gray-600 toggle-att-employeeSalary"
            >
              <ol className="breadcrumb">
                {breadcrumbs.map((crumb, idx) => (
                  <li
                    key={idx}
                    className={`breadcrumb-item ${
                      idx === breadcrumbs.length - 1 ? "active" : ""
                    }`}
                    onClick={crumb.onClick}
                    style={{ cursor: crumb.onClick ? "pointer" : "default" }}
                  >
                    {crumb.title}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>
      </div>
    );
    const ProfileDesktopFilters = () => (
      <div className="px-2 px-md-2">
        <div className="designation-filters d-flex align-items-center flex-wrap ">
          <div className="d-flex align-items-center gap-2  toggle-att-employeeSalarymedia toggle-att-employeeSalarydesktop">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mt-3 text-sm text-gray-600">
              <ol className="breadcrumb">
                {breadcrumbs.map((crumb, idx) => (
                  <li
                    key={idx}
                    className={`breadcrumb-item ${
                      idx === breadcrumbs.length - 1 ? "active" : ""
                    }`}
                    onClick={crumb.onClick}
                    style={{ cursor: crumb.onClick ? "pointer" : "default" }}
                  >
                    {crumb.title}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>
      </div>
    );

    setFilters(
      isMobileView ? <ProfileMobileFilters /> : <ProfileDesktopFilters />
    );
    return () => {
      console.log("cleaning up filters");
      setFilters(null);
    };
  }, [isMobileView]);

  const openCropModal = () => {
    if (!modalRef.current) return;
    // create or re-use the Modal instance
    modalInstance.current = new Modal(modalRef.current);
    modalInstance.current.show();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      openCropModal();
    }
  };

  const handleSaveCrop = async () => {
    if (!editorRef.current) return;

    try {
      // 1. Extract the cropped image as a data URL
      const canvasDataUrl = editorRef.current
        .getImageScaledToCanvas()
        .toDataURL();

      // 2. Convert to Blob so we can append it to FormData
      const blob = await (await fetch(canvasDataUrl)).blob();

      // 3. Build FormData payload
      const formData = new FormData();
      formData.append("profile_image", blob, "avatar.png");
      formData.append("id", employeeId);

      // 4. POST to your save-image endpoint
      const res = await axiosInstance.post<{
        message: string;
        file_path: string;
        original_size: number;
        compressed_size: number;
      }>("/save-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // res.data now contains your JSON
      const { message, file_path } = res.data;

      // show the API‐returned message
      toast.success(message);

      // if you prefer to use the server’s returned file URL:
      setImageUrl(file_path);
      //  Get the logged-in user
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const loggedInUser = JSON.parse(storedUser);

      //  Only update if the logged-in user is updating their own image
      if (String(loggedInUser.id) === String(employeeId)) {
        loggedInUser.image_url = file_path;
        localStorage.setItem("user", JSON.stringify(loggedInUser));

        //  Also update context (if using context)
        if (user) {
          const updatedUser = { ...user, image_url: file_path };
          setUser(updatedUser);
        }
      }
    }
      // hide the modal
      modalInstance.current?.hide();
    } catch (err: any) {
      toast.error(err.message || "Something went wrong while saving image.");
    }
  };

  //   if (loading)
  //     return <div className="text-center py-4">Loading profile...</div>;

  return (
    <>
      <div className="card-height1">
        {loading ? (
          <div className="card mb-4 border-0">
            <div className="card-body text-center card-height card-height1">
              <div
                className="placeholder rounded-circle "
                style={{ width: 120, height: 120 }}
              />

              {/* <div className="mt-2">Loading profile...</div> */}
            </div>
          </div>
        ) : (
          // {/* Profile Header + Crop */}
          <div className="card mb-4 border-0">
            <div
              className="card-body text-center"
              style={{ minHeight: 211.56 }} // ← set minimum fixed height to prevent shrinking
            >
              <div
                className="position-relative d-inline-block"
                style={{ width: 120, height: 120 }} // ensure consistent image wrapper height
              >
                {!imageLoaded && (
                  <div
                    className="placeholder rounded-circle bg-light"
                    style={{ width: 120, height: 120 }}
                  />
                )}
                <img
                  src={imageUrl || "/default-avatar.png"}
                  className="rounded-circle"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    display: imageLoaded ? "block" : "none",
                  }}
                  alt={employee?.name || passedName}
                  onLoad={() => setImageLoaded(true)}
                />
                <label
                  htmlFor="file-input"
                  className="position-absolute bottom-0 end-0 bg-white rounded-circle p-2"
                  style={{ cursor: "pointer" }}
                >
                  <FaCamera />
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>
              {imageLoaded && (
                <>
                  <h5 className="mt-2">{employee?.name || passedName}</h5>
                  <small className="text-muted d-block">
                    {employee?.designation?.title || passedDesignation}
                  </small>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Crop Modal */}
      <div
        ref={modalRef}
        className="modal fade"
        id="edit_profile_photo"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Profile Photo</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body text-center">
              {selectedFile && (
                <>
                  <AvatarEditor
                    ref={editorRef}
                    image={selectedFile}
                    width={250}
                    height={250}
                    border={50}
                    borderRadius={125}
                    color={[255, 255, 255, 0.6]}
                    scale={scale}
                    rotate={0}
                  />
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={scale}
                    onChange={(e) => setScale(parseFloat(e.target.value))}
                    className="mt-3 w-100"
                  />
                </>
              )}
            </div>
            <div className="modal-footer">
              <Button data-bs-dismiss="modal">Cancel</Button>
              <Button type="primary" onClick={handleSaveCrop}>
                Save &amp; Crop
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Additional fields tabs if needed... */}
    </>
  );
};

export default EmployeeImage;
